import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  EmptyState,
  IconSearch,
  Modal,
  SearchInput,
  Spinner,
} from '@bala/ui';
import {
  ACTIVITY_TYPE_LABELS,
  ageAt,
  formatShortDate,
  fullName,
  matchesQuery,
  relevanceScore,
  TASK_STATUS_LABELS,
  type Activity,
  type Baladin,
  type DocumentItem,
  type Game,
  type ParentMessage,
  type TaskItem,
  type User,
} from '@bala/core';
import { useDebounced, useQuery } from '../api';
import { useAuth } from '../auth';

/**
 * Recherche globale : un seul champ qui interroge tous les modules auxquels
 * l'animateur a accès.
 *
 * Les jeux de données d'une section tiennent en mémoire, donc la recherche se
 * fait côté client sur les listes déjà en cache. Conséquence utile : elle
 * fonctionne hors connexion, sans requête réseau.
 */

interface Hit {
  id: string;
  kind: string;
  kindLabel: string;
  title: string;
  subtitle: string;
  to: string;
  score: number;
}

export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 150);

  // Les listes ne sont chargées qu'à la première ouverture du panneau.
  const enabled = open;
  const baladins = useQuery<{ items: Baladin[] }>(
    enabled && auth.can('baladins') ? '/baladins' : null,
    ['baladins'],
  );
  const animateurs = useQuery<{ items: User[] }>(
    enabled && auth.can('animateurs') ? '/animateurs' : null,
    ['users'],
  );
  const activities = useQuery<{ items: Activity[] }>(
    enabled && auth.can('calendar') ? '/activities' : null,
    ['activities'],
  );
  const games = useQuery<{ items: Game[] }>(enabled && auth.can('games') ? '/games' : null, ['games']);
  const tasks = useQuery<{ items: TaskItem[] }>(enabled && auth.can('tasks') ? '/tasks' : null, ['tasks']);
  const documents = useQuery<{ items: DocumentItem[] }>(
    enabled && auth.can('documents') ? '/documents' : null,
    ['documents'],
  );
  const messages = useQuery<{ items: ParentMessage[] }>(
    enabled && auth.can('messages') ? '/messages' : null,
    ['parent_messages'],
  );

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const loading =
    baladins.loading || animateurs.loading || activities.loading || games.loading || tasks.loading;

  const hits = useMemo<Hit[]>(() => {
    const term = debounced.trim();
    if (term.length < 2) return [];
    const output: Hit[] = [];

    const push = (fields: Array<string | null | undefined>, hit: Omit<Hit, 'score'>) => {
      if (!matchesQuery(term, fields)) return;
      output.push({ ...hit, score: relevanceScore(term, fields) });
    };

    for (const baladin of baladins.data?.items ?? []) {
      const age = ageAt(baladin.birthDate);
      push([fullName(baladin), baladin.allergies, baladin.address], {
        id: baladin.id,
        kind: 'baladin',
        kindLabel: 'Baladin',
        title: fullName(baladin),
        subtitle: age != null ? `${age} ans` : '',
        to: `/liste/baladins?focus=${baladin.id}`,
      });
    }

    for (const user of animateurs.data?.items ?? []) {
      push([user.totem, fullName(user), user.email], {
        id: user.id,
        kind: 'animateur',
        kindLabel: 'Animateur',
        title: user.totem || fullName(user),
        subtitle: user.totem ? fullName(user) : user.email,
        to: `/liste/animateurs?focus=${user.id}`,
      });
    }

    for (const activity of activities.data?.items ?? []) {
      push([activity.title, activity.description, activity.locationText, activity.meetingLocationName, activity.meetingLocationAddress], {
        id: activity.id,
        kind: 'activite',
        kindLabel: ACTIVITY_TYPE_LABELS[activity.type],
        title: activity.title,
        subtitle: formatShortDate(activity.startAt),
        to: `/calendrier/${activity.id}`,
      });
    }

    for (const game of games.data?.items ?? []) {
      push([game.name, game.tags.join(' '), game.description, game.materialToBuy.join(' ')], {
        id: game.id,
        kind: 'jeu',
        kindLabel: 'Jeu',
        title: game.name,
        subtitle: [
          game.durationMinutes ? `${game.durationMinutes} min` : null,
          game.inventoryItemIds.length > 0 ? `${game.inventoryItemIds.length} objet(s)` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        to: `/jeux/${game.id}`,
      });
    }

    for (const task of tasks.data?.items ?? []) {
      push([task.title, task.description], {
        id: task.id,
        kind: 'tache',
        kindLabel: 'Tâche',
        title: task.title,
        subtitle: TASK_STATUS_LABELS[task.status],
        to: `/taches/${task.id}`,
      });
    }

    for (const document of documents.data?.items ?? []) {
      push([document.title, document.description, document.tags.join(' ')], {
        id: document.id,
        kind: 'document',
        kindLabel: 'Document',
        title: document.title,
        subtitle: document.kind.toUpperCase(),
        to: `/documents?focus=${document.id}`,
      });
    }

    for (const message of messages.data?.items ?? []) {
      push([message.subject, message.body], {
        id: message.id,
        kind: 'message',
        kindLabel: 'Message parents',
        title: message.subject,
        subtitle: message.publishedAt ? `Publié le ${formatShortDate(message.publishedAt)}` : 'Brouillon',
        to: `/communications/${message.id}`,
      });
    }

    return output.sort((a, b) => b.score - a.score).slice(0, 40);
  }, [
    debounced,
    baladins.data,
    animateurs.data,
    activities.data,
    games.data,
    tasks.data,
    documents.data,
    messages.data,
  ]);

  const grouped = useMemo(() => {
    const groups = new Map<string, Hit[]>();
    for (const hit of hits) {
      const bucket = groups.get(hit.kindLabel) ?? [];
      bucket.push(hit);
      groups.set(hit.kindLabel, bucket);
    }
    return [...groups];
  }, [hits]);

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  return (
    <Modal open={open} onClose={onClose} title="Rechercher" width={620} variant="dialog">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
        <SearchInput
          value={query}
          onChange={setQuery}
          autoFocus
          placeholder="Un baladin, une réunion, un jeu, une tâche…"
        />

        {query.trim().length < 2 ? (
          <EmptyState
            compact
            icon={<IconSearch size={28} />}
            title="Cherchez dans toute l'application"
            description="Tapez au moins deux caractères. La recherche couvre les baladins, les animateurs, le calendrier, les jeux, les tâches, les documents et les messages — même hors connexion."
          />
        ) : hits.length === 0 ? (
          loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s-6)' }}>
              <Spinner size={20} color="var(--accent)" />
            </div>
          ) : (
            <EmptyState compact icon={<IconSearch size={26} />} title="Aucun résultat" description={`Rien ne correspond à « ${query} ».`} />
          )
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
            {grouped.map(([label, items]) => (
              <section key={label}>
                <h3
                  style={{
                    font: 'var(--t-caption)',
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    marginBottom: 'var(--s-2)',
                  }}
                >
                  {label}
                </h3>
                <ul style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {items.map((hit) => (
                    <li key={`${hit.kind}-${hit.id}`}>
                      <button
                        type="button"
                        onClick={() => go(hit.to)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--s-3)',
                          width: '100%',
                          padding: '9px 11px',
                          borderRadius: 'var(--r-md)',
                          textAlign: 'left',
                          transition: 'background var(--d-fast)',
                        }}
                        onMouseEnter={(event) => {
                          event.currentTarget.style.background = 'var(--surface-hover)';
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', font: 'var(--t-subhead)', fontWeight: 500 }} className="bala-truncate">
                            {hit.title}
                          </span>
                          {hit.subtitle && (
                            <span
                              style={{ display: 'block', font: 'var(--t-caption)', color: 'var(--text-secondary)' }}
                              className="bala-truncate"
                            >
                              {hit.subtitle}
                            </span>
                          )}
                        </span>
                        <Badge tone="gray" size="sm">
                          {hit.kindLabel}
                        </Badge>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
